import { useMemo } from "react";
import { cn } from "../../lib/cn";
import { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH, TOP_NAV_HEIGHT } from "./constants";
import type { NavItem } from "./types";

export function NavSideBar({
  currentPath,
  isCollapsed,
  isVisible,
  items,
  mode,
  onClose,
  onLocationChange,
}: {
  currentPath: string;
  isCollapsed: boolean;
  isVisible: boolean;
  items: NavItem[];
  mode: "fixed" | "drawer";
  onClose: () => void;
  onLocationChange: (to: string) => void;
}) {
  const width = isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;
  const topOffset = mode === "drawer" ? TOP_NAV_HEIGHT : 0;
  const menuItems = useMemo(() => items, [items]);

  return (
    <>
      {mode === "drawer" && (
        <div
          className={cn("drawer-backdrop", isVisible && "drawer-backdrop-visible")}
          style={{ top: topOffset, height: `calc(100vh - ${topOffset}px)` }}
          onClick={onClose}
        />
      )}
      <aside
        className={cn("nav-sidebar", mode === "drawer" && !isVisible && "-translate-x-full")}
        style={{
          top: topOffset,
          height: mode === "drawer" ? `calc(100vh - ${topOffset}px)` : "100vh",
          width,
          transition: "width 250ms ease-in-out, transform 250ms ease-in-out",
        }}
        aria-hidden={mode === "drawer" ? !isVisible : undefined}
      >
        {mode !== "drawer" && (
          <button
            className={cn("nav-brand", isCollapsed && "nav-brand--collapsed")}
            type="button"
            onClick={() => onLocationChange("/")}
          >
            <span className={cn("nav-brand-logo", isCollapsed && "nav-brand-logo--collapsed")}>
              <img src="/assets/Litmus_Logo_Reverse.png" alt="Litmus Logo" className="nav-brand-full" />
              <img src="/assets/Litmus_Logo.png" alt="Litmus" className="nav-brand-icon" />
            </span>
          </button>
        )}

        <nav className={cn("nav-menu", isCollapsed ? "px-1.5 py-2" : "px-2.5 py-3")}>
          {menuItems.map((item) => {
            const active =
              item.to === "/"
                ? currentPath === "/"
                : currentPath === item.to || currentPath.startsWith(item.to + "/");
            return (
              <button
                key={item.to}
                type="button"
                title={isCollapsed ? item.label : undefined}
                onClick={() => onLocationChange(item.to)}
                className={cn(
                  "nav-menu-item",
                  active && "nav-menu-item-selected",
                  isCollapsed && "nav-menu-item-collapsed",
                )}
              >
                <span className="nav-menu-icon">{item.icon(active)}</span>
                {!isCollapsed && <span className="min-w-0 truncate">{item.label}</span>}
              </button>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
